/*
 *
 * AdminPage / Users / NewUser
 *
 * Create-user form (super_admin only). POSTs to /api/v3/admin/users with a
 * `user` object containing the fields the controller permits: first_name,
 * last_name, email, password, company, permission_role (integer enum). On
 * success it toasts and routes to the new user's detail; on failure it surfaces
 * the server's validation errors inline.
 *
 * Kept as a small controlled form rather than pulling in react-hook-form/yup —
 * six fields with server-side validation don't warrant the extra machinery.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { t } from "copy/t";

import { useCreateAdminUser } from "../api/mutations";
import { ASSIGNABLE_ROLE_OPTIONS } from "./roles";
import {
  Wrapper,
  BackLink,
  Form,
  FormRow,
  FieldLabel,
  TextInput,
  RoleSelect,
  SubmitButton,
  FormError,
} from "./styledComponents";

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  company: "",
  permission_role: "",
};

// Flatten the rails error payload ({ errors: { field: [msgs] } } or
// { error: "msg" }) into a single readable string for the inline banner.
const formatErrors = (err) => {
  if (!err) return "Couldn’t create user. Try again.";
  if (err.errors && typeof err.errors === "object") {
    const parts = Object.entries(err.errors).map(
      ([field, msgs]) => `${field} ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`
    );
    if (parts.length) return parts.join("; ");
  }
  return err.error || "Couldn’t create user. Try again.";
};

const NewUser = () => {
  const navigate = useNavigate();
  const [values, setValues] = useState(EMPTY);
  const [submitError, setSubmitError] = useState(null);
  const { mutate, isLoading } = useCreateAdminUser();

  const setField = (name) => (e) =>
    setValues((prev) => ({ ...prev, [name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Only send fields the user filled. permission_role is coerced to the
    // integer enum the controller expects; an unset role is omitted so the
    // model default applies.
    const payload = {
      email: values.email,
      password: values.password,
    };
    if (values.first_name) payload.first_name = values.first_name;
    if (values.last_name) payload.last_name = values.last_name;
    if (values.company) payload.company = values.company;
    if (values.permission_role !== "") {
      payload.permission_role = Number(values.permission_role);
    }

    mutate(payload, {
      onSuccess: (user) => {
        toast.success(t("toast.admin.userCreated"));
        navigate(`/admin/users/${user.id}`);
      },
      onError: (err) => {
        const message = formatErrors(err);
        setSubmitError(message);
        toast.error(message);
      },
    });
  };

  const canSubmit = values.email.trim() && values.password.trim() && !isLoading;

  return (
    <Wrapper>
      <BackLink type="button" onClick={() => navigate("/admin/users")}>
        ← Back to users
      </BackLink>
      <h1>New user</h1>

      <Form onSubmit={onSubmit}>
        {submitError && <FormError role="alert">{submitError}</FormError>}

        <FormRow>
          <FieldLabel htmlFor="new-user-email">Email *</FieldLabel>
          <TextInput
            id="new-user-email"
            type="email"
            required
            value={values.email}
            onChange={setField("email")}
          />
        </FormRow>

        <FormRow>
          <FieldLabel htmlFor="new-user-password">Password *</FieldLabel>
          <TextInput
            id="new-user-password"
            type="password"
            required
            value={values.password}
            onChange={setField("password")}
          />
        </FormRow>

        <FormRow>
          <FieldLabel htmlFor="new-user-first">First name</FieldLabel>
          <TextInput
            id="new-user-first"
            type="text"
            value={values.first_name}
            onChange={setField("first_name")}
          />
        </FormRow>

        <FormRow>
          <FieldLabel htmlFor="new-user-last">Last name</FieldLabel>
          <TextInput
            id="new-user-last"
            type="text"
            value={values.last_name}
            onChange={setField("last_name")}
          />
        </FormRow>

        <FormRow>
          <FieldLabel htmlFor="new-user-company">Company</FieldLabel>
          <TextInput
            id="new-user-company"
            type="text"
            value={values.company}
            onChange={setField("company")}
          />
        </FormRow>

        <FormRow>
          <FieldLabel htmlFor="new-user-role">Role</FieldLabel>
          <RoleSelect
            id="new-user-role"
            value={values.permission_role}
            onChange={setField("permission_role")}
          >
            <option value="">Default (photographer)</option>
            {ASSIGNABLE_ROLE_OPTIONS.map((r) => (
              <option key={r.key} value={r.value}>
                {r.label}
              </option>
            ))}
          </RoleSelect>
        </FormRow>

        <SubmitButton type="submit" disabled={!canSubmit}>
          {isLoading ? "Creating…" : "Create user"}
        </SubmitButton>
      </Form>
    </Wrapper>
  );
};

export default NewUser;
